import { Plus } from 'lucide-react';
import { Link } from 'react-router-dom';

/**
 * Botón de acción principal para CREAR cosas (pedido, cliente, producto...).
 *
 * Existe para que todos los "Nuevo X" de la app sean identicos y se localicen
 * de un vistazo. Antes el estilo estaba copiado en una docena de pantallas, con
 * variantes distintas (rounded-md vs rounded-lg, py-2 vs py-2.5), y el unico
 * efecto al pasar por encima era bajar la opacidad: no destacaba sobre nada.
 *
 * Si hay que retocar el aspecto, se toca AQUI y cambia en toda la aplicacion.
 *
 * Uso:
 *   <CreateButton href="/pedidos/nuevo">Nuevo Pedido</CreateButton>
 *   <CreateButton onClick={abrirModal}>Nueva Oferta</CreateButton>
 *   <CreateButton onClick={x} icon={ShoppingCart}>Nuevo Pedido</CreateButton>
 */

// Color de marca. Coincide con defaultTheme.primary de ThemeContext.
const BRAND = '#0099FF';

const BASE_CLASSES = [
  'inline-flex items-center justify-center gap-2',
  'px-5 py-2.5 rounded-lg',
  'text-sm font-semibold text-white whitespace-nowrap',
  // duration-150: la respuesta tiene que sentirse inmediata, no perezosa.
  'transition-all duration-150 ease-out',
  // Se levanta y brilla un poco al pasar por encima: deja claro que es LA accion.
  'hover:-translate-y-0.5 hover:brightness-110',
  // Al pulsar vuelve a su sitio, para dar sensacion de clic fisico.
  'active:translate-y-0 active:brightness-95',
  // Accesibilidad: foco visible para quien navega con teclado.
  'focus:outline-none focus-visible:ring-4 focus-visible:ring-sky-300',
  'disabled:opacity-50 disabled:cursor-not-allowed',
  'disabled:hover:translate-y-0 disabled:hover:brightness-100',
].join(' ');

// La sombra de color es lo que realmente hace que el boton "flote" sobre la
// pagina y se encuentre rapido. Va en linea porque depende del color de marca.
const SHADOW = '0 4px 14px rgba(0, 153, 255, 0.40)';
const SHADOW_HOVER = '0 6px 20px rgba(0, 153, 255, 0.55)';

export default function CreateButton({
  children,
  to,
  href,
  onClick,
  icon: Icon = Plus,
  disabled = false,
  className = '',
  type = 'button',
  ...rest
}) {
  const style = { backgroundColor: BRAND, boxShadow: SHADOW };

  // La sombra se refuerza en hover via JS porque Tailwind no puede interpolar
  // un box-shadow de color arbitrario sin ensuciar la configuracion.
  const handlers = disabled ? {} : {
    onMouseEnter: (e) => { e.currentTarget.style.boxShadow = SHADOW_HOVER; },
    onMouseLeave: (e) => { e.currentTarget.style.boxShadow = SHADOW; },
  };

  const content = (
    <>
      {Icon && <Icon size={18} strokeWidth={2.5} />}
      {children}
    </>
  );

  const classes = `${BASE_CLASSES} ${className}`;

  // `to` es navegacion interna de react-router: NO debe usar <a href>, que
  // provocaria una recarga completa de la SPA.
  if (to && !disabled) {
    return (
      <Link to={to} style={style} className={classes} {...handlers} {...rest}>
        {content}
      </Link>
    );
  }

  // Si se pasa href es navegacion: debe ser un enlace de verdad, para que
  // funcionen "abrir en pestaña nueva" y el clic con el boton central.
  if (href && !disabled) {
    return (
      <a href={href} style={style} className={classes} {...handlers} {...rest}>
        {content}
      </a>
    );
  }

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      style={style}
      className={classes}
      {...handlers}
      {...rest}
    >
      {content}
    </button>
  );
}
